import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { baseURL } from '../common'
import { setUserDetails } from './userSlice'

// Centralized RTK Query API slice. New data-fetching code should add endpoints here and
// use the auto-generated hooks (e.g. `useGetCurrentUserQuery`) instead of hand-rolling
// fetch calls — that gives us caching, dedup, loading/error state, and cache invalidation
// by tag for free.
//
// Migration is incremental: existing fetch-based code keeps working until each endpoint
// gets ported. The auth flow (httpOnly JWT cookie via `credentials: 'include'`) matches
// the axios instance in `utils/Axios.js`. A 401 from any RTKQ-hosted endpoint clears the
// local user state (mirroring the axios interceptor) so guarded UI updates immediately.

export const API_TAG_TYPES = ['User', 'Product', 'Order', 'Category', 'Banner', 'Wishlist', 'Stock', 'Subscription', 'Cart']

const rawBaseQuery = fetchBaseQuery({
    baseUrl: baseURL,
    credentials: 'include',
})

const baseQueryWithAuth = async (args, api, extraOptions) => {
    const result = await rawBaseQuery(args, api, extraOptions)
    if (result?.error?.status === 401) {
        try { api.dispatch(setUserDetails(null)) } catch (_) {}
    }
    return result
}

export const api = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithAuth,
    tagTypes: API_TAG_TYPES,
    endpoints: (builder) => ({
        // Current user — first endpoint migration. Returns `null` on 401 (anonymous) so
        // consumers can treat "no user" as a normal state rather than an error. Tagged
        // 'User' so mutations like login/logout invalidate the cache.
        getCurrentUser: builder.query({
            query: () => ({ url: '/api/v1/user-details', method: 'GET' }),
            transformResponse: (response) => response?.data || null,
            providesTags: ['User'],
            // 401 propagates as `isError` to the component; the auth-wrapper above
            // already clears local user state.
        }),

        // Cart count — second endpoint. Used by the header badge for authenticated
        // users. Guest counts come from the cartSlice's `selectCartCount` selector.
        getCartCount: builder.query({
            query: () => ({ url: '/api/v1/countAddToCartProduct', method: 'GET' }),
            transformResponse: (response) => Number(response?.data?.count || 0),
            providesTags: ['Cart'],
        }),
    }),
})

export const { useGetCurrentUserQuery, useGetCartCountQuery } = api

export default api
